/**
 * macOS Safari 26 does not select text that has a CSS transform. Chrome on
 * the same Mac does. pdf.js 4.10 writes `transform: scaleX(..) scale(..)`
 * on every multi-character span, and the `scale()` is added whenever the
 * measured 1px font is taller than 1px — which is the macOS WebKit minimum
 * font, not Linux WebKit. Playwright's WebKit therefore still selects.
 */

export function isSafariBrowser(userAgent = navigator.userAgent): boolean {
  return /Safari/i.test(userAgent) && !/Chrome|Chromium|CriOS|Edg|OPR|FxiOS|Firefox/i.test(userAgent);
}

export function parseTextLayerTransform(transform: string): { scaleX: number; scale: number } {
  let scaleX = 1;
  let scale = 1;
  for (const match of transform.matchAll(/scaleX\(\s*([0-9.eE+-]+)\s*\)/g)) {
    const value = Number(match[1]);
    if (Number.isFinite(value) && value > 0) scaleX *= value;
  }
  const withoutX = transform.replace(/scaleX\(\s*[0-9.eE+-]+\s*\)/g, '');
  for (const match of withoutX.matchAll(/scale\(\s*([0-9.eE+-]+)\s*\)/g)) {
    const value = Number(match[1]);
    if (Number.isFinite(value) && value > 0) scale *= value;
  }
  return { scaleX, scale };
}

export interface SpanBakeRecord {
  text: string;
  inlineFont: string;
  inlineTransform: string;
  originalWidth: string;
  originalHeight: string;
  originalLetterSpacing: string;
  appliedFont: string;
  appliedWidth: string;
  appliedHeight: string;
  keptOriginal: boolean;
}

export interface SafariTextLayerPrep {
  safari: boolean;
  transformsCleared: number;
  ancestorsCleared: string[];
  restored: number;
  spans: SpanBakeRecord[];
}

/** One pdf.js text item, in CSS pixels at the page's viewport scale. */
export interface SafariTextRun {
  text: string;
  fontPx: number;
  widthPx: number;
}

export function safariTextRuns(items: readonly unknown[], viewportScale: number): SafariTextRun[] {
  if (!(viewportScale > 0)) return [];
  const runs: SafariTextRun[] = [];
  for (const item of items) {
    if (typeof item !== 'object' || item === null || !('str' in item)) continue;
    const str = item.str;
    if (typeof str !== 'string' || str === '') continue;
    const transform = 'transform' in item ? item.transform : undefined;
    const rawWidth = 'width' in item ? item.width : undefined;
    const coords = textRunCoords(transform);
    const font =
      coords && coords.length >= 4 ? Math.hypot(coords[2] ?? 0, coords[3] ?? 0) * viewportScale : 0;
    const width = typeof rawWidth === 'number' ? rawWidth * viewportScale : 0;
    runs.push({
      text: str,
      fontPx: font > 0 ? font : 0,
      widthPx: width > 0 ? width : 0,
    });
  }
  return runs;
}

function textRunCoords(value: unknown): ArrayLike<number> | null {
  if (Array.isArray(value)) return value as number[];
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView(value) && !(value instanceof DataView)) {
    return value as unknown as ArrayLike<number>;
  }
  return null;
}

type MeasureText = (text: string, fontSizePx: number, fontFamily: string) => number;

const lastPrep = new WeakMap<HTMLElement, SafariTextLayerPrep>();

export function safariPrepFor(textLayer: HTMLElement): SafariTextLayerPrep | undefined {
  return lastPrep.get(textLayer);
}

export function prepareSafariTextLayer(
  textLayer: HTMLElement,
  userAgent = navigator.userAgent,
  measure: MeasureText = measureTextOn2dCanvas,
  runs: readonly SafariTextRun[] = [],
): SafariTextLayerPrep {
  if (!isSafariBrowser(userAgent)) {
    return { safari: false, transformsCleared: 0, ancestorsCleared: [], restored: 0, spans: [] };
  }
  const ancestorsCleared = clearAncestorUserSelectNone(textLayer);
  textLayer.style.setProperty('-webkit-user-select', 'text', 'important');
  textLayer.style.setProperty('user-select', 'text', 'important');
  const scaleFactor = Number(textLayer.style.getPropertyValue('--scale-factor'));
  const spans: SpanBakeRecord[] = [];
  let transformsCleared = 0;
  let runIndex = 0;
  for (const span of textLayer.querySelectorAll<HTMLElement>('span')) {
    span.style.setProperty('-webkit-user-select', 'text', 'important');
    span.style.setProperty('user-select', 'text', 'important');
    const text = span.textContent ?? '';
    const candidate = span.classList.contains('markedContent') ? undefined : runs[runIndex];
    if (candidate && candidate.text === text) runIndex += 1;
    const record = bakeTransformIntoBox(
      span,
      scaleFactor,
      measure,
      candidate && candidate.text === text ? candidate : undefined,
    );
    spans.push(record);
    if (!record.keptOriginal) transformsCleared += 1;
  }
  const result = { safari: true, transformsCleared, ancestorsCleared, restored: 0, spans };
  lastPrep.set(textLayer, result);
  return result;
}

/**
 * A baked span that is still 0×0 after layout is put back to the pdf.js
 * transform. A 0 or NaN box is never left in place.
 */
export function restoreUnselectableBakes(textLayer: HTMLElement): number {
  const prep = lastPrep.get(textLayer);
  if (!prep) return 0;
  const spans = [...textLayer.querySelectorAll<HTMLElement>('span')];
  let restored = 0;
  spans.forEach((span, index) => {
    const record = prep.spans[index];
    if (!record || record.keptOriginal) return;
    const box = span.getBoundingClientRect();
    if (box.width >= 0.5 && box.height >= 0.5) return;
    span.style.fontSize = record.inlineFont;
    span.style.transform = record.inlineTransform;
    span.style.width = record.originalWidth;
    span.style.height = record.originalHeight;
    span.style.letterSpacing = record.originalLetterSpacing;
    record.keptOriginal = true;
    record.appliedFont = record.inlineFont;
    record.appliedWidth = record.originalWidth;
    record.appliedHeight = record.originalHeight;
    restored += 1;
  });
  prep.restored = restored;
  prep.transformsCleared = prep.spans.filter((row) => !row.keptOriginal).length;
  return restored;
}

/**
 * pdf.js sets `font-size: calc(var(--scale-factor)*Npx)` and
 * `transform: scaleX(a) scale(b)`. This used to read getComputedStyle and
 * offsetWidth while the layer was still detached (PdfCanvasPreview calls
 * this before append). Safari resolves that calc to 0px off-document and
 * offsetWidth is 0, then the transform was cleared anyway, so every span
 * stayed 0×0. Sizes now come from the inline calc and a DOM 2D canvas.
 * A 0 or NaN result is not written.
 */
function bakeTransformIntoBox(
  span: HTMLElement,
  scaleFactor: number,
  measure: MeasureText,
  run?: SafariTextRun,
): SpanBakeRecord {
  const inlineFont = span.style.fontSize;
  const inlineTransform = span.style.transform;
  const text = span.textContent ?? '';
  const base: SpanBakeRecord = {
    text: text.slice(0, 40),
    inlineFont,
    inlineTransform,
    originalWidth: span.style.width,
    originalHeight: span.style.height,
    originalLetterSpacing: span.style.letterSpacing,
    appliedFont: inlineFont,
    appliedWidth: span.style.width,
    appliedHeight: span.style.height,
    keptOriginal: true,
  };
  if (!inlineTransform || inlineTransform === 'none' || /rotate\(/i.test(inlineTransform)) return base;
  const { scaleX, scale } = parseTextLayerTransform(inlineTransform);
  const fromRun = run && run.fontPx > 0 && run.widthPx > 0 ? run : undefined;
  let fontSize: number | null = fromRun ? fromRun.fontPx : null;
  if (fontSize == null) {
    const unscaledFont = fontPxFromInline(inlineFont, scaleFactor);
    if (unscaledFont == null || !(scale > 0) || !(scaleX > 0)) return base;
    fontSize = unscaledFont * scale;
  }
  const fontPx = positivePx(fontSize);
  if (!fontPx || !text) return base;
  const family = span.style.fontFamily || 'sans-serif';
  const natural = measure(text, fontSize, family);
  const width = fromRun ? fromRun.widthPx : natural * scaleX;
  const widthPx = positivePx(width);
  const heightPx = positivePx(fontSize);
  if (!widthPx || !heightPx) return base;
  const letterSpacing = text.length > 1 && natural > 0 ? (width - natural) / (text.length - 1) : 0;
  if (!Number.isFinite(letterSpacing)) return base;

  span.style.fontSize = fontPx;
  span.style.width = widthPx;
  span.style.height = heightPx;
  span.style.backgroundColor = 'transparent';
  span.style.letterSpacing =
    letterSpacing !== 0 && Math.abs(letterSpacing) <= fontSize ? `${letterSpacing}px` : '';
  span.style.transform = 'none';
  return {
    ...base,
    appliedFont: fontPx,
    appliedWidth: widthPx,
    appliedHeight: heightPx,
    keptOriginal: false,
  };
}

/** `calc(var(--scale-factor)*Npx)` times the numeric --scale-factor, or a plain px size. */
export function fontPxFromInline(fontSize: string, scaleFactor: number): number | null {
  const factorFirst = fontSize.match(
    /calc\(\s*var\(\s*--scale-factor\s*\)\s*\*\s*([0-9.eE+-]+)px\s*\)/i,
  );
  const pxFirst = fontSize.match(
    /calc\(\s*([0-9.eE+-]+)px\s*\*\s*var\(\s*--scale-factor\s*\)\s*\)/i,
  );
  const calc = factorFirst ?? pxFirst;
  if (calc) {
    const n = Number(calc[1]);
    if (!(n > 0) || !(scaleFactor > 0)) return null;
    return n * scaleFactor;
  }
  if (!fontSize.trim().endsWith('px')) return null;
  const px = Number.parseFloat(fontSize);
  return px > 0 ? px : null;
}

function positivePx(value: number): string | null {
  if (!Number.isFinite(value) || !(value > 0)) return null;
  return `${value}px`;
}

function measureTextOn2dCanvas(text: string, fontSizePx: number, fontFamily: string): number {
  if (!(fontSizePx > 0) || !text) return 0;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return 0;
  context.font = `${fontSizePx}px ${fontFamily}`;
  const width = context.measureText(text).width;
  return Number.isFinite(width) && width > 0 ? width : 0;
}

/**
 * WebKit inherits `-webkit-user-select: none` and ignores a descendant's
 * `user-select: text`. Force `text` on each ancestor that computed to none,
 * up to and including the preview root.
 */
export function clearAncestorUserSelectNone(textLayer: HTMLElement): string[] {
  const cleared: string[] = [];
  const stop = textLayer.closest('.fp-viewer');
  let node: HTMLElement | null = textLayer;
  while (node) {
    const webkit = getComputedStyle(node).getPropertyValue('-webkit-user-select');
    const standard = getComputedStyle(node).getPropertyValue('user-select') || getComputedStyle(node).userSelect;
    if (webkit === 'none' || standard === 'none') {
      node.style.setProperty('-webkit-user-select', 'text', 'important');
      node.style.setProperty('user-select', 'text', 'important');
      cleared.push(node.className || node.tagName);
    }
    if (node === stop) break;
    node = node.parentElement;
  }
  return cleared;
}
